import {useEffect, useRef} from "react";
import {observer} from "mobx-react";
import {withServices} from "../../react-hooks/with-services";
import {ComponentServicesPick} from "../../services/component-services-pick";
import {GameInfo} from "../../services/games/game-info";
import {JackpotToastWrapperBox} from "./boxes/jackpot-toast-wrapper.box";
import {GameImageContainerComponent} from "../single-game/common/parts/game-image-container.component/game-image-container.component";
import {GameImageContainerBox} from "../single-game/regular-template/boxes/game-image-container/game-image-container.box";
import {JackpotToastCurrencyBox} from "./boxes/time-and-amount/jackpot-toast-currency.box";
import {JackpotToastSuggestedGameBox} from "./boxes/suggested-games/jackpot-toast-suggested-game.box";
import {JackpotToastSuggestedGamesTitleBox} from "./boxes/suggested-games/jackpot-toast-suggested-games-title.box";
import {JackpotToastSuggestedGamesWrapperBox} from "./boxes/suggested-games/jackpot-toast-suggested-games-wrapper.box";
import {JackpotToastLogoAndTitleBox} from "./boxes/logo-and-title/jackpot-toast-logo-and-title.box";
import {JackpotToastLogoBox} from "./boxes/logo-and-title/jackpot-toast-logo.box";
import {AmountAnimator} from "../../../utils/amount-animator/amount-animator";
import {TimeSpan} from "@sparkware/app-utils";
import {NullableDate} from "../../../common/types/nullable-types";
import {JackpotToastTimeAndAmountBox} from "./boxes/time-and-amount/jackpot-toast-time-and-amount.box";
import {JackpotToastClockBox} from "./boxes/time-and-amount/jackpot-toast-clock.box";
import {JackpotToastTitleWrapperBox} from "./boxes/logo-and-title/jackpot-toast-title-wrapper.box";
import {JackpotToastTitleAnimatedBox} from "./boxes/logo-and-title/jackpot-toast-title-animated.box";
import {IToastHandler} from "../../services/toaster/toaster.service.interface";
import {onToastOpenedAnalytics} from "../../services/toaster/toast-helpers/toast-analytics.helper";
// import {OrbitMarketingEventAnalyticsCategory} from "../../services/analytics/analytics.service.interface";

export interface JackpotToastComponentProps extends ComponentServicesPick<'theme' | 'jackpots' | 'currency' | 'time' | 'logger' | 'user' | 'analytics'> {
    handler: IToastHandler;
    logoImage: string;
    gamesInfo: GameInfo[];
    mainTitle: string;
    secondTitle: string;
    message: string;
    amount: number;
    mustHitTime?: NullableDate;
}

const ComponentBody: React.FC<JackpotToastComponentProps> = observer((props) => {
    let amountAnimatorRef = useRef<AmountAnimator>(null);
    useEffect(() => {
        const amountAnimator = new AmountAnimator(() => props.amount);
        // animate from 3/4 of the final amount but if the difference is bigger than 5000.00 start start from amount - 500000
        const fromAmount = Math.max(Math.round( 3 * props.amount / 4), props.amount - 500000) ;

        amountAnimator.startAnimation({
            animationDuration: TimeSpan.fromSeconds(2.5),
            iterationDuration: TimeSpan.fromMilliseconds(60),
            fromAmount: fromAmount
        });
        amountAnimatorRef.current = amountAnimator;
    }, [props.amount]);

    const getClockValue = () => {

        const mustHitDate = props.mustHitTime;

        if(!mustHitDate) {
            return '';
        }

        const currentDate = props.time.getCurrentDate();
        const seconds = Math.round((mustHitDate.getTime() - currentDate.getTime())/1000);
        const remainingTimeTimeSp = TimeSpan.fromSeconds(Math.max(0, seconds));
        return remainingTimeTimeSp.toString({useTotalHours: true});
    }
    
    // I put the analytics inside an useEffect because I want to make sure it is run once, at mount, not every rerender
    // handler shouldn't change so it shouldn't rerun at component update
    useEffect(() => {
        onToastOpenedAnalytics({user: props.user, analytics: props.analytics}, props.handler.toastId);
    }, [props.handler, props.user, props.analytics]);

    const renderGame = (gameInfo: GameInfo) => {
        return <JackpotToastSuggestedGameBox key={gameInfo.gameType}>
            <GameImageContainerComponent gameInfo={gameInfo}
                box={GameImageContainerBox}
                onOpenRealGame={() => {
                    // const cid = props.user.cid || 'annonymous';
                    // props.analytics.sendOrbitMarketingEvent(OrbitMarketingEventAnalyticsCategory.Communications, props.handler.toastId + '_CTA_Click', cid + '_' + Date.now().toString());

                    gameInfo.open({
                        isRealMode: true
                    });
                    props.handler.close();
                }}
                useSquareIcons={false}
                playRealGameButton={null}
                disableAnimation={true}
            />
        </JackpotToastSuggestedGameBox>
    }

    const renderMustHitTime = () => {
        if(!props.mustHitTime) {
            return null;
        }
        return <JackpotToastClockBox>
            {getClockValue()}
        </JackpotToastClockBox>
    }

    return <JackpotToastWrapperBox>
        <JackpotToastLogoAndTitleBox>
            <div style={{textAlign: "center"}}>
                <JackpotToastLogoBox src={props.logoImage} alt="jackpot-logo" />
            </div>
            <JackpotToastTitleWrapperBox>
                <JackpotToastTitleAnimatedBox>{props.mainTitle}</JackpotToastTitleAnimatedBox>
                <JackpotToastTitleAnimatedBox>{props.secondTitle}</JackpotToastTitleAnimatedBox>
            </JackpotToastTitleWrapperBox>
        </JackpotToastLogoAndTitleBox>
        <JackpotToastTimeAndAmountBox>
            {renderMustHitTime()}
            <JackpotToastCurrencyBox>
                {props.currency.formatMoney(props.mustHitTime ? amountAnimatorRef.current?.currentValue : props.amount)}
            </JackpotToastCurrencyBox>
        </JackpotToastTimeAndAmountBox>
        <JackpotToastSuggestedGamesTitleBox style={{textAlign: "center"}}>{props.message}</JackpotToastSuggestedGamesTitleBox>
        <JackpotToastSuggestedGamesWrapperBox>
            {props.gamesInfo.map(renderGame)}
        </JackpotToastSuggestedGamesWrapperBox>
    </JackpotToastWrapperBox>
});

export const JackpotToastComponent = withServices(ComponentBody, ['theme', 'jackpots', 'currency', 'time', 'logger', 'user', 'analytics']);